import { DateRanges } from '../../forms/date-ranges/DateRanges'
import React, { useEffect, useMemo, useState } from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import moment from 'moment'
import { BpSelect } from '../../forms/select/BpSelect'
import { GroupByOptions } from '../../../consts/GroupByOptions.const'
import { EDateGroup } from '../../../enums/DateGroup.enum'
import { IDateRange } from '../../../types/DateRange.interface'
import { useDateRange } from '../../../lib/hooks/UseDateRange'
import { EDateRangeType } from '../../../../pages/advertising-research/model/types/DateRangeType.enum'
import { useTranslation } from 'react-i18next'
import { Tooltip as BpTooltip } from '../../text/tooltip/Tooltip'
import { IChartModel } from '../../../../app/api'

const CHART_TIMEZONE_OFFSET = 180
const DEFAULT_CHART_COLORS = [
  '#7cb5ec',
  '#434348',
  '#90ed7d',
  '#f7a35c',
  '#8085e9',
  '#f15c80',
  '#e4d354',
  '#2b908f',
  '#f45b5b',
  '#91e8e1',
]

const AXIS_TICK_STYLE = {
  fill: 'rgba(0,50,109,0.3)',
  fontSize: 13,
  fontFamily: 'Jost, sans-serif',
}

type ChartDataPoint = {
  timestamp: number
  [seriesName: string]: number
}

export interface IGraphReportParams {
  series?: Array<IChartModel & { color?: string }>
  onChange?: (e: IDateRange) => void
  name: string
  title: string | null
  header?: React.ReactNode
  filter: IDateRange
  groupSelector?: boolean
  exceptionsDates?: EDateRangeType[]
  reversed?: boolean
  yTickInterval?: number
  yMin?: number
  yMax?: number
}

const formatChartTooltipDate = (timestamp: number) =>
  moment.utc(timestamp).utcOffset(CHART_TIMEZONE_OFFSET).format('dddd, D MMM')

const formatChartAxisDate = (timestamp: number) =>
  moment.utc(timestamp).utcOffset(CHART_TIMEZONE_OFFSET).format('D MMM')

const mergeSeriesData = (series: Array<IChartModel & { color?: string }>): ChartDataPoint[] => {
  const map = new Map<number, ChartDataPoint>()

  series.forEach((seriesItem) => {
    if (!seriesItem.data || !seriesItem.name) return

    seriesItem.data.forEach(([timestamp, value]) => {
      const existing = map.get(timestamp) ?? { timestamp }
      existing[seriesItem.name] = value
      map.set(timestamp, existing)
    })
  })

  return Array.from(map.values()).sort((a, b) => a.timestamp - b.timestamp)
}

const getSeriesColor = (seriesItem: IChartModel & { color?: string }, index: number): string => {
  if (typeof seriesItem.color === 'string') return seriesItem.color

  return DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length]
}

const getYAxisTicks = (
  chartData: ChartDataPoint[],
  series: Array<IChartModel & { color?: string }> | undefined,
  yMin?: number,
  yMax?: number,
  yTickInterval?: number,
): number[] | undefined => {
  if (yTickInterval === undefined || yMin === undefined) return undefined

  let maxValue = yMax

  if (maxValue === undefined) {
    maxValue = yMin

    series?.forEach(({ name }) => {
      if (!name) return

      chartData.forEach((point) => {
        const value = point[name]
        if (typeof value === 'number' && value > maxValue!) {
          maxValue = value
        }
      })
    })
  }

  const ticks: number[] = []

  for (let value = yMin; value <= maxValue; value += yTickInterval) {
    ticks.push(value)
  }

  return ticks
}

export const GraphReport = ({
  series,
  onChange,
  name,
  title,
  header,
  filter,
  groupSelector = true,
  exceptionsDates,
  reversed,
  yTickInterval,
  yMin,
  yMax,
}: IGraphReportParams) => {
  const { t } = useTranslation()
  const [hiddenSeriesNames, setHiddenSeriesNames] = useState<string[]>([])
  const [filters, setFilters] = useState<IDateRange>(filter)
  const { date, group } = filters

  const chartData = useMemo(() => mergeSeriesData(series ?? []), [series])
  const yAxisTicks = useMemo(
    () => getYAxisTicks(chartData, series, yMin, yMax, yTickInterval),
    [chartData, series, yMin, yMax, yTickInterval],
  )

  useEffect(() => {
    setHiddenSeriesNames((prevHidden) =>
      prevHidden.filter((hiddenName) => series?.some(({ name }) => name === hiddenName)),
    )
  }, [series])

  useEffect(() => {
    if (onChange) onChange(filters)
  }, [date, group])

  const maxVisibleLegendItems = 5
  const legendItems =
    series?.map((seriesItem, index) => {
      const legendName = seriesItem.name || `Series ${index + 1}`

      return {
        legendName,
        seriesItem,
        index,
        isHidden: hiddenSeriesNames.includes(legendName),
      }
    }) || []
  const visibleLegendItems = legendItems.slice(0, maxVisibleLegendItems)
  const hiddenLegendItems = legendItems.slice(maxVisibleLegendItems)

  return (
    <section className='flex w-full flex-col'>
      <h3 className='mb-[16px] flex flex-col justify-between gap-[16px] lg:flex-row lg:items-center'>
        <span className='font-medium'>{header || title}</span>

        <span className='flex flex-col gap-[27px] md:flex-row md:items-center'>
          {groupSelector && (
            <BpSelect
              hasTranslate
              defaultValue={group}
              fieldSize='sm'
              options={GroupByOptions}
              title={t('fields.groupBy')}
              onChange={(e) => setFilters({ ...filters, group: e.target.value as EDateGroup })}
            />
          )}

          <DateRanges
            dateRange={date}
            exceptions={exceptionsDates}
            name={name}
            onChange={(e) =>
              setFilters({
                ...filters,
                date: useDateRange(EDateRangeType[e.target.value as EDateRangeType]).date,
              })
            }
          />
        </span>
      </h3>
      {!!series?.length && (
        <div className='mb-[16px] flex flex-wrap items-center gap-x-[24px] gap-y-[10px]'>
          {visibleLegendItems.map(({ legendName, seriesItem, index, isHidden }) => (
            <button
              key={legendName}
              className='flex items-center gap-[8px] text-[15px] text-[#0E63D8] disabled:cursor-not-allowed'
              type='button'
              onClick={() =>
                setHiddenSeriesNames((prevHidden) =>
                  isHidden
                    ? prevHidden.filter((hiddenName) => hiddenName !== legendName)
                    : [...prevHidden, legendName],
                )
              }
            >
              <span
                style={{
                  backgroundColor: getSeriesColor(seriesItem, index),
                  opacity: isHidden ? 0.35 : 1,
                }}
                className='h-[16px] w-[16px] rounded-[16px]'
              />
              <span className={isHidden ? 'opacity-40' : ''}>{legendName}</span>
            </button>
          ))}
          {!!hiddenLegendItems.length && (
            <div className={'select-none'}>
              <BpTooltip title={hiddenLegendItems.map(({ legendName }) => legendName).join(', ')}>
                <p className={'rounded-[4px] bg-[#E8EDF2] px-[6px] py-[2px]'}>
                  <span className={'text-[12px] font-medium text-[#6684A7]'}>
                    +{hiddenLegendItems.length}
                  </span>
                </p>
              </BpTooltip>
            </div>
          )}
        </div>
      )}
      <div className='relative h-[300px]'>
        <div className={`h-full ${!series?.length && 'hidden'}`}>
          <ResponsiveContainer height='100%' width='100%'>
            <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke='rgba(0,50,109,0.03)' vertical={false} />
              <XAxis
                axisLine={false}
                dataKey='timestamp'
                domain={['dataMin', 'dataMax']}
                scale='time'
                tick={{ ...AXIS_TICK_STYLE }}
                tickFormatter={formatChartAxisDate}
                tickLine={false}
                type='number'
              />
              <YAxis
                allowDecimals={yTickInterval !== 1}
                axisLine={false}
                domain={[yMin ?? 'auto', yMax ?? 'auto']}
                reversed={reversed}
                tick={{ ...AXIS_TICK_STYLE }}
                tickLine={false}
                ticks={yAxisTicks}
                width={40}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length || typeof label !== 'number') return null

                  return (
                    <div className='rounded-[15px] bg-white px-[12px] py-[8px] text-[14px] text-[#00326D] shadow-sm'>
                      <p className='mb-[6px] font-medium'>{formatChartTooltipDate(label)}</p>
                      {payload.map((item) => (
                        <div key={item.dataKey} className='flex items-center gap-[8px]'>
                          <span
                            className='h-[8px] w-[8px] rounded-full'
                            style={{ backgroundColor: item.color }}
                          />
                          <span>{item.name}</span>
                          <span className='font-medium'>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  )
                }}
              />
              {series?.map((seriesItem, index) => {
                const seriesName = seriesItem.name

                if (!seriesName) return null

                const seriesColor = getSeriesColor(seriesItem, index)

                return (
                  <Line
                    key={seriesName}
                    connectNulls
                    dot={{
                      r: 4,
                      fill: seriesColor,
                      stroke: seriesColor,
                      strokeWidth: 0,
                    }}
                    activeDot={{ r: 5 }}
                    dataKey={seriesName}
                    hide={hiddenSeriesNames.includes(seriesName)}
                    name={seriesName}
                    stroke={seriesColor}
                    strokeWidth={2}
                    type='linear'
                  />
                )
              })}
            </LineChart>
          </ResponsiveContainer>
        </div>
        {!series?.length && (
          <div className='absolute inset-0 flex h-full flex-col items-center justify-center gap-[40px]'>
            <svg
              fill='none'
              height='85'
              viewBox='0 0 78 85'
              width='78'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M30 77.0004L35.892 80.2736C37.0264 80.904 37.5936 81.2188 38.1944 81.3424C38.726 81.452 39.274 81.452 39.806 81.3424C40.4064 81.2188 40.9736 80.904 42.108 80.2736L48 77.0004M12 67.0004L6.29188 63.8292C5.09384 63.1636 4.49472 62.8308 4.05852 62.3572C3.67264 61.9388 3.3806 61.4424 3.20192 60.9016C3 60.2904 3 59.6052 3 58.2344V52.0004M3 32.0004V25.7661C3 24.3956 3 23.7103 3.20192 23.0991C3.3806 22.5584 3.67264 22.062 4.05852 21.6433C4.49472 21.1699 5.0938 20.8371 6.29188 20.1715L12 17.0004M30 7.00032L35.892 3.72708C37.0264 3.09684 37.5936 2.78172 38.1944 2.65816C38.726 2.54884 39.274 2.54884 39.806 2.65816C40.4064 2.78172 40.9736 3.09684 42.108 3.72708L48 7.00032M66 17.0003L71.708 20.1715C72.9064 20.8371 73.5052 21.1699 73.9416 21.6433C74.3272 22.062 74.6196 22.5584 74.798 23.0991C75 23.7103 75 24.3956 75 25.7661V32.0003M75 52.0004V58.2344C75 59.6052 75 60.2904 74.798 60.9016C74.6196 61.4424 74.3272 61.9388 73.9416 62.3572C73.5052 62.8308 72.9064 63.1636 71.708 63.8292L66 67.0004M30 37.0004L39 42.0004M39 42.0004L48 37.0004M39 42.0004V52.0004M3 22.0003L12 27.0003M66 27.0003L75 22.0003M39 72.0004V82.0004'
                stroke='#6684A7'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='5'
              />
            </svg>
            <span className='text-[24px] font-medium text-aqua-300'>
              {t('other.noResultsFound')}
            </span>
          </div>
        )}
      </div>
    </section>
  )
}
